import React, {PropsWithChildren, useMemo} from 'react'
import {ApolloClient, ApolloLink, ApolloProvider, createHttpLink, from, InMemoryCache} from '@apollo/client'
import {onError} from '@apollo/client/link/error'
import {setContext} from '@apollo/client/link/context'
import {getAuth} from 'firebase/auth'

import fragmentMatcher from './generated/fragmentMatcher'
import {getCommitSHA, getGraphQLEndpoint} from './common/environment/environmentManager'
import {paginatedField} from './apollo/helper/paginatedField'
import {GQL_AllRentalsResponse, GQL_AllUsersResponse, GQL_Rental, GQL_User} from './generated/graphql'

const APP_NAME = 'piffl-admin'
const APP_VERSION = getCommitSHA() || 'unknown'

// Reusable field policy: keep existing non-empty string if incoming is empty/null/undefined.
// This prevents UI flicker when different queries write partial data.
const stringFieldPreferExistingIfEmpty = () => ({
  merge(existing: string | null | undefined, incoming: string | null | undefined) {
    const isEmpty = (v: any) => v === null || v === undefined || (typeof v === 'string' && v.trim() === '')
    if (!isEmpty(existing) && isEmpty(incoming)) return existing
    return incoming
  },
})

const cache = new InMemoryCache({
  possibleTypes: fragmentMatcher.possibleTypes,
  typePolicies: {
    ActivityAreaSearchRoot: {merge: true},
    BatteryReadingRoot: {merge: true},
    BoxSearchRoot: {merge: true},
    CompartmentSearchRoot: {merge: true},
    FeedbackSearchRoot: {merge: true},
    PaymentSearchRoot: {merge: true},
    PendingUserSearchRoot: {merge: true},
    PermissionSearchRoot: {merge: true},
    ProductSearchRoot: {merge: true},
    OrganisationSearchRoot: {merge: true},
    RentalSearchRoot: {
      merge: true,
      fields: {
        allRentals: paginatedField<GQL_AllRentalsResponse, GQL_Rental>(['filter', 'search']),
      },
    },
    ReviewSearchRoot: {merge: true},
    RoleSearchRoot: {merge: true},
    SponsorSearchRoot: {merge: true},
    UserSearchRoot: {
      merge: true,
      fields: {
        allUsers: paginatedField<GQL_AllUsersResponse, GQL_User>(['filter', 'search']),
      },
    },
    ViewerSearchRoot: {merge: true},

    // --- IMPORTANT: WHY THESE MERGE POLICIES EXIST -------------------------------------------
    //
    // We have multiple queries that normalize the same Box + Location entities but
    // with *different* field selections. Example:
    //
    //   - Query A writes: Box { id, location { id, name, description } }
    //   - Query B writes: Box { id, location { id, municipality, locality } }
    //
    // Apollo will normalize both objects to the same Location cache record (by id).
    // When Query B arrives, fields it does NOT select (e.g. name/description) can be
    // replaced by "", null, or simply omitted from the incoming object. That can
    // temporarily blank-out previously rendered values, causing the visible “flash”.
    //
    // To prevent that, we do two things:
    //  1) Treat Location objects as embedded under Box (we keep Location keyFields loose),
    //  2) Provide a custom merge for Box.location that *refuses to overwrite* non-empty
    //     user-facing strings (name, description, municipality, locality) with empty values.
    //
    // This is a pragmatic workaround. It avoids flickering without forcing all queries
    // across the app to request the exact same Location fields in the exact same order.
    // If you add more user-visible fields to Location, consider adding them to the
    // `preferExistingIfEmpty` set below.
    //
    // Additionally, we apply the same “prefer existing if empty” rule directly on Box
    // fields (name, description, municipality, locality) to stop Header/Link flicker.

    Location: {keyFields: false},
    Box: {
      fields: {
        // Prevent Box.name/description/municipality/locality flicker (e.g., BoxLink text)
        name: stringFieldPreferExistingIfEmpty(),
        description: stringFieldPreferExistingIfEmpty(),
        municipality: stringFieldPreferExistingIfEmpty(),
        locality: stringFieldPreferExistingIfEmpty(),

        location: {
          merge(existing = null, incoming) {
            if (!incoming) return existing
            if (!existing) return incoming

            const merged: any = {...existing}

            // For these fields, don't overwrite with an empty string
            const preferExistingIfEmpty = new Set(['name', 'description', 'municipality', 'locality'])

            // Note on `translations` (jsonb on Location): partial-Location queries (e.g. the inline
            // selections in allBoxLocationsLite, BoxesPageViewForLite, actionsBoxes, failedOpenings)
            // don't select `translations`, so it's absent from `incoming` and naturally preserved
            // by the for-loop below. Queries that DO select translations carry the full sub-shape
            // via the `location` fragment, so they overwrite cleanly. No special-casing needed —
            // the null-skip below covers it. If you add an inline Location selection that DOES
            // include `translations`, make sure it requests every supported language so the row's
            // all-or-nothing claim isn't half-populated in cache.
            for (const [key, value] of Object.entries(incoming)) {
              if (value === null || value === undefined) {
                continue
              }
              if (
                typeof value === 'string' &&
                value.trim() === '' &&
                preferExistingIfEmpty.has(key) &&
                typeof existing[key] === 'string' &&
                existing[key].trim() !== ''
              ) {
                // keep the existing non-empty value
                continue
              }
              merged[key] = value
            }

            return merged
          },
        },
      },
    },

    PaymentChargeInformation: {keyFields: false},
  },
})

const apolloURI = getGraphQLEndpoint() || '/graphql'
if (!getGraphQLEndpoint()) {
  console.log('No graphql endpoint was provided')
}

export const AuthorizedApolloProvider: React.FC<PropsWithChildren<{}>> = ({children}) => {
  // Pure HTTP link
  const httpLink = useMemo(() => createHttpLink({uri: apolloURI}), [])

  // Auth header
  const authLink = useMemo(
    () =>
      setContext(async (_, {headers}) => {
        const token = await getAuth().currentUser?.getIdToken()
        const t = typeof token === 'string' ? token.trim() : ''
        return {
          headers: {
            ...headers,
            // Avoid sending empty Authorization header (caused Firebase token decode errors)
            // See Sentry issue: PIFFL-SERVER-S
            ...(t ? {authorization: `Bearer ${t}`} : {}),
            'apollographql-client-name': APP_NAME,
            'apollographql-client-version': APP_VERSION,
            'x-client-kind': 'admin',
          },
        }
      }),
    [],
  )

  // Timing
  const timingLink = useMemo(
    () =>
      new ApolloLink((operation, forward) => {
        const DEBUG = localStorage.getItem('gqlDebug') === '1'
        const start = performance.now()
        const name = operation.operationName || 'anonymous'
        const fp = (operation.getContext()?.fetchOptions as any)?.fetchPolicy || operation.getContext()?.fetchPolicy

        const SLOW_MS = 500

        const varsStr = DEBUG ? JSON.stringify(operation.variables ?? {}) : ''
        const reqKB = DEBUG ? (varsStr ? (varsStr.length / 1024).toFixed(1) : '0.0') : ''

        if (DEBUG) {
          console.groupCollapsed(`[GQL][${name}] start ${fp ? `(${fp})` : ''}`)
          console.log('variables', operation.variables)
          if (reqKB) console.log('~request size', `${reqKB} KB`)
          performance.mark?.(`gql:${name}:start`)
        } else {
          console.log(`[GQL][${name}] start ${fp ? `(${fp})` : ''}`)
        }

        return forward(operation).map((result) => {
          const ms = performance.now() - start
          const fromCache = operation.getContext()?.response === undefined && !(result as any)?.extensions

          const status = result?.errors?.length ? `ERR(${result.errors.length})` : 'OK'
          let resKB = ''
          if (DEBUG) {
            const dataStr = JSON.stringify(result?.data ?? {})
            resKB = (dataStr.length / 1024).toFixed(1)
          }

          const tag = fromCache ? 'cache' : 'network'
          const baseMsg = `[GQL][${name}] ${ms.toFixed(1)} ms ${status} [${tag}]`

          if (DEBUG) {
            console.log(baseMsg)
            if (resKB) console.log('~response size', `${resKB} KB`)
            if (result?.errors?.length) console.warn('errors', result.errors)
            performance.mark?.(`gql:${name}:end`)
            performance.measure?.(`gql:${name}`, `gql:${name}:start`, `gql:${name}:end`)
            console.groupEnd()
          } else {
            if (ms > SLOW_MS) console.warn(`${baseMsg} ⚠️ slow`)
            else console.log(baseMsg)
          }
          return result
        })
      }),
    [],
  )

  // Error logging
  const errorLink = useMemo(
    () =>
      onError(({graphQLErrors, networkError, operation}) => {
        const name = operation.operationName || 'anonymous'
        if (graphQLErrors && graphQLErrors.length) {
          graphQLErrors.forEach((e) => {
            console.error(`[GQL][${name}] GraphQL error:`, e.message, e)
          })
        }
        if (networkError) {
          console.error(`[GQL][${name}] Network error:`, networkError)
        }
      }),
    [],
  )

  const client = useMemo(
    () =>
      new ApolloClient({
        link: from([errorLink, timingLink, authLink.concat(httpLink)]),
        cache,
        defaultOptions: {
          watchQuery: {fetchPolicy: 'cache-first'},
          query: {fetchPolicy: 'cache-first'},
          mutate: {errorPolicy: 'all'},
        },
      }),
    [errorLink, timingLink, authLink, httpLink],
  )

  return <ApolloProvider client={client}>{children}</ApolloProvider>
}
